// src/services/supplierService.js

import apiClient from '../api/apiClient';

/**
 * Fetch all suppliers with pagination support.
 * @param {string} orgId - The organization ID.
 * @param {Object} options - Pagination and filtering options.
 * @returns {Promise<Object>} Promise resolving to paginated suppliers data.
 */
export const getSuppliers = async (orgId, options = {}) => {
  const intOrgId = parseInt(orgId, 10);
  const params = new URLSearchParams();
  
  // Add pagination parameters if provided
  if (options.page) params.append('page', options.page);
  if (options.limit) params.append('limit', options.limit);
  if (options.search !== undefined && options.search !== null) params.append('search', options.search);
  if (options.sortBy) params.append('sortBy', options.sortBy);
  if (options.sortOrder) params.append('sortOrder', options.sortOrder);
  if (options.dateFrom) params.append('dateFrom', options.dateFrom);
  if (options.dateTo) params.append('dateTo', options.dateTo);
  
  const queryString = params.toString();
  const url = `/suppliers/org/${intOrgId}${queryString ? `?${queryString}` : ''}`;
  
  return apiClient.get(url)
    .then(response => response.data);
};

/**
 * Add a new supplier.
 * @param {Object} data - The supplier data.
 * @returns {Promise<void>}
 */
export const addSupplier = async (orgId, data) => {
  const intOrgId = parseInt(orgId, 10);
  return apiClient.post('/suppliers', { ...data, org_id: intOrgId })
    .then(response => response.data);
};

/**
 * Find a supplier by ID.
 * @param {string} supplierId - The supplier ID to search for.
 * @returns {Promise<Object|null>} Promise resolving to the found supplier or null.
 */
export const getSupplierById = async (orgId, supplierId) => {
  return apiClient.get(`/suppliers/${supplierId}`)
    .then(response => response.data);
};

/**
 * Delete multiple suppliers.
 * @param {Array<string>} supplierIds - The IDs of the suppliers to delete.
 * @returns {Promise<void>}
 */
export const deleteMultipleSuppliers = (supplierIds) => {
  return apiClient.post('/suppliers/bulk-delete', { supplier_ids: supplierIds })
    .then(response => response.data);
};

/**
 * Bulk create suppliers.
 * @param {Object} data - The supplier data.
 * @returns {Promise<void>}
 */
export const bulkCreateSuppliers = (data) => {
  return apiClient.post('/suppliers/bulk', data).then(response => response.data);
};
